import { defineStore } from 'pinia';
import { z } from 'zod';
import { computed, ref } from 'vue';
import { cloneDeep } from 'lodash-es';
import { StoreNames } from '../../../shared/store-names';
import { useAccountStore } from '../../account';
import { selectFilterValidationSchema, multiSelectFilterValidationSchema } from '../../filters';
import { useDataTableSettings } from '../../../composables';
import { useUiStatesApi, useUiStateAutoPersist } from '../../ui-states';
import { LocationsColumns as Columns } from '../shared/locations-columns';
import type { LocationsFiltersState } from '../interfaces';

const UI_STATES_KEY = StoreNames.InventoryLocationsPage;

export const useInventoryLocationsPageStore = defineStore(StoreNames.InventoryLocationsPage, () => {
  const defaultAppliedFilters: LocationsFiltersState = {
    location_type: ['in', []],
    region: ['in', []],
    city: ['in', []],
    brands: ['in', []],
    classifications: ['in', []],
    avoid_replenishment: ['eq', null],
  };

  const appliedFilters = ref<LocationsFiltersState>(cloneDeep(defaultAppliedFilters));

  const filtersValidationSchema = z.object({
    location_type: multiSelectFilterValidationSchema,
    region: multiSelectFilterValidationSchema,
    city: multiSelectFilterValidationSchema,
    brands: multiSelectFilterValidationSchema,
    classifications: multiSelectFilterValidationSchema,
    avoid_replenishment: selectFilterValidationSchema,
  });

  const quickFilters = ref<string[]>([]);

  const customColumns = ref<string[]>([]);

  // TODO: move filters to useDataTableSettings?

  const {
    columnsVisibility,
    columnsWidth,
    columnsOrder,
    visibleColumns,
    resetColumnsSettings,
    applyPersistedState,
    columnsSettingsTouched,
    pinnedColumns,
    sorting,
    resetSorting,
    handleColumnResize,
    userColumnsWidth,
  } = useDataTableSettings({
    defaultColumnsVisibility: {
      [Columns.Name]: true,
      [Columns.ExternalId]: false,
      [Columns.LocationType]: true,
      [Columns.Description]: false,
      [Columns.Region]: false,
      [Columns.City]: true,
      [Columns.Brands]: true,
      [Columns.Classifications]: true,
      [Columns.AtSite]: true,
      [Columns.InTransit]: true,
      [Columns.Reserved]: true,
      [Columns.NumOfProducts]: false,
      [Columns.NumOfSkus]: false,
      [Columns.InventoryCost]: false,
      [Columns.InventoryPrice]: false,
      [Columns.AvoidReplenishment]: true,
      [Columns.CreatedAt]: false,
    },
    defaultColumnsWidth: {
      [Columns.Name]: 168,
      [Columns.ExternalId]: 168,
      [Columns.LocationType]: 168,
      [Columns.Description]: 256,
      [Columns.Region]: 256,
      [Columns.City]: 168,
      [Columns.Brands]: 168,
      [Columns.Classifications]: 168,
      [Columns.AtSite]: 168,
      [Columns.InTransit]: 168,
      [Columns.Reserved]: 168,
      [Columns.NumOfProducts]: 168,
      [Columns.NumOfSkus]: 168,
      [Columns.InventoryCost]: 168,
      [Columns.InventoryPrice]: 168,
      [Columns.AvoidReplenishment]: 168,
      [Columns.CreatedAt]: 168,
    },
    defaultColumnsOrder: [
      Columns.Name,
      Columns.ExternalId,
      Columns.LocationType,
      Columns.Description,
      Columns.Region,
      Columns.City,
      Columns.Brands,
      Columns.Classifications,
      Columns.AtSite,
      Columns.InTransit,
      Columns.Reserved,
      Columns.NumOfProducts,
      Columns.NumOfSkus,
      Columns.InventoryCost,
      Columns.InventoryPrice,
      Columns.AvoidReplenishment,
      Columns.CreatedAt,
    ],
    defaultPinnedColumns: [Columns.Name, Columns.ExternalId],
    customColumns,
  });

  function reset() {
    resetColumnsSettings();
    resetSorting();
    appliedFilters.value = cloneDeep(defaultAppliedFilters);
  }

  const api = useUiStatesApi();
  const accountStore = useAccountStore();

  const fetching = ref(false);
  const fetched = ref(false);

  let fetchingPromise: Promise<void> | null = null;

  async function fetch(force = false) {
    if (fetched.value && !force) {
      return fetchingPromise ?? Promise.resolve();
    }

    if (!fetchingPromise) {
      fetchingPromise = (async () => {
        fetching.value = true;

        const [persistedState] = await Promise.all([
          api.getUiStates({ key: UI_STATES_KEY }).then(({ data }) => data.data[0]),
          accountStore.fetch(),
        ]);

        customColumns.value =
          accountStore.customDataSchema.Location?.map(({ key }) => `custom_data.${key}`) ?? [];

        reset();

        // Apply persisted state

        if (persistedState) {
          applyPersistedState(persistedState.value);

          if (persistedState.value.appliedFilters) {
            try {
              appliedFilters.value = filtersValidationSchema.parse(
                persistedState.value.appliedFilters,
              );
            } catch (error) {
              // do nothing
            }
          }

          if (persistedState.value.quickFilters) {
            quickFilters.value = persistedState.value.quickFilters;
          }
        }

        fetching.value = false;
        fetched.value = true;
        fetchingPromise = null;
      })();
    }

    return fetchingPromise;
  }

  const persistingValue = computed(() => ({
    columnsVisibility: { ...columnsVisibility.value },
    columnsWidth: { ...userColumnsWidth.value },
    columnsOrder: [...columnsOrder.value],
    pinnedColumns: [...pinnedColumns.value],
    sorting: { ...sorting.value },
    appliedFilters: { ...appliedFilters.value },
    quickFilters: [...quickFilters.value],
  }));

  useUiStateAutoPersist({
    enabled: fetched,
    value: persistingValue,
    key: UI_STATES_KEY,
  });

  return {
    fetching,
    fetched,
    fetch,
    reset,
    resetColumnsSettings,
    columnsVisibility,
    columnsWidth,
    visibleColumns,
    columnsOrder,
    pinnedColumns,
    sorting,
    appliedFilters,
    columnsSettingsTouched,
    handleColumnResize,
    resetSorting,
    quickFilters,
  };
});
